/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RiskDetectionScope,
  RiskDetectionScope$inboundSchema,
} from "./riskdetectionscope.js";
import { RiskMCPScope, RiskMCPScope$inboundSchema } from "./riskmcpscope.js";
import {
  RiskPolicyApprovalConfig,
  RiskPolicyApprovalConfig$inboundSchema,
} from "./riskpolicyapprovalconfig.js";
import {
  RiskPolicyApprovalsServerSetup,
  RiskPolicyApprovalsServerSetup$inboundSchema,
} from "./riskpolicyapprovalsserversetup.js";
import {
  RiskPolicyModelConfig,
  RiskPolicyModelConfig$inboundSchema,
} from "./riskpolicymodelconfig.js";

/**
 * Policy action: flag (log only), warn (challenge: warn the user and require acknowledgement to proceed), block (deny in real-time), quarantine (deny and freeze the hook session), or require_approval (hold the call until a listed approver approves it). MCP-scoped policies support flag, block and require_approval.
 */
export const RiskPolicyAction = {
  Flag: "flag",
  Warn: "warn",
  Block: "block",
  Quarantine: "quarantine",
  RequireApproval: "require_approval",
} as const;
/**
 * Policy action: flag (log only), warn (challenge: warn the user and require acknowledgement to proceed), block (deny in real-time), quarantine (deny and freeze the hook session), or require_approval (hold the call until a listed approver approves it). MCP-scoped policies support flag, block and require_approval.
 */
export type RiskPolicyAction = OpenEnum<typeof RiskPolicyAction>;

/**
 * Policy audience type: everyone or targeted.
 */
export const RiskPolicyAudienceType = {
  Everyone: "everyone",
  Targeted: "targeted",
} as const;
/**
 * Policy audience type: everyone or targeted.
 */
export type RiskPolicyAudienceType = OpenEnum<typeof RiskPolicyAudienceType>;

/**
 * Policy type: standard (regex/presidio/custom detection) or prompt_based (LLM-judge).
 */
export const RiskPolicyPolicyType = {
  Standard: "standard",
  PromptBased: "prompt_based",
} as const;
/**
 * Policy type: standard (regex/presidio/custom detection) or prompt_based (LLM-judge).
 */
export type RiskPolicyPolicyType = OpenEnum<typeof RiskPolicyPolicyType>;

/**
 * Default disposition for shadow MCP blocking policies: block_all blocks every non-Speakeasy-hosted server unless allowed, allow_all permits every server unless blocked. Blocked URLs are stored as risk_policy:block grants, not on the policy. Immutable after create. Only present on policies with the shadow_mcp source and block action.
 */
export const RiskPolicyShadowMcpDisposition = {
  BlockAll: "block_all",
  AllowAll: "allow_all",
} as const;
/**
 * Default disposition for shadow MCP blocking policies: block_all blocks every non-Speakeasy-hosted server unless allowed, allow_all permits every server unless blocked. Blocked URLs are stored as risk_policy:block grants, not on the policy. Immutable after create. Only present on policies with the shadow_mcp source and block action.
 */
export type RiskPolicyShadowMcpDisposition = OpenEnum<
  typeof RiskPolicyShadowMcpDisposition
>;

export type RiskPolicy = {
  /**
   * Policy action: flag (log only), warn (challenge: warn the user and require acknowledgement to proceed), block (deny in real-time), quarantine (deny and freeze the hook session), or require_approval (hold the call until a listed approver approves it). MCP-scoped policies support flag, block and require_approval.
   */
  action: RiskPolicyAction;
  approvalConfig?: RiskPolicyApprovalConfig | undefined;
  approvalsServerSetup?: RiskPolicyApprovalsServerSetup | undefined;
  /**
   * For the account_identity source: corporate email domains considered approved. Sessions whose AI-account email domain is not listed are flagged. Empty means the domain rule is inert.
   */
  approvedEmailDomains?: Array<string> | undefined;
  /**
   * Principal URNs the policy applies to. Contains user:all when audience_type is everyone.
   */
  audiencePrincipalUrns: Array<string>;
  /**
   * Policy audience type: everyone or targeted.
   */
  audienceType: RiskPolicyAudienceType;
  /**
   * Whether the policy name is auto-generated. When true, the name is regenerated on each update.
   */
  autoName: boolean;
  /**
   * When the policy was created.
   */
  createdAt: Date;
  /**
   * Custom detection rule ids attached as detectors: a match produces a finding. Custom rules are pure detectors.
   */
  customRuleIds?: Array<string> | undefined;
  /**
   * Per-category detection scopes specified for this policy. The scan surface merges these with the recommended scopes, the specified scope winning on category conflict. Empty means every recommendation applies unchanged.
   */
  detectionScopes?: Array<RiskDetectionScope> | undefined;
  /**
   * Canonical rule_ids (e.g. 'secret.aws_access_token', 'pii.credit_card') the policy author has unchecked within an otherwise-enabled category. Empty means every rule in the selected categories runs; matching findings are dropped at scan time.
   */
  disabledRules?: Array<string> | undefined;
  /**
   * Whether the policy is active.
   */
  enabled: boolean;
  /**
   * The risk policy ID.
   */
  id: string;
  mcpScope?: RiskMCPScope | undefined;
  modelConfig?: RiskPolicyModelConfig | undefined;
  /**
   * The policy name.
   */
  name: string;
  /**
   * Policy type: standard (regex/presidio/custom detection) or prompt_based (LLM-judge).
   */
  policyType: RiskPolicyPolicyType;
  /**
   * Presidio entity types to scan for. When empty, scans all entities.
   */
  presidioEntities?: Array<string> | undefined;
  /**
   * Minimum Presidio confidence (0.0-1.0) a PII match must clear to surface. Omit/null applies the default (0.5).
   */
  presidioScoreThreshold?: number | undefined;
  /**
   * The project ID.
   */
  projectId: string;
  /**
   * For prompt_based policies: the guardrail prompt the LLM judge evaluates each in-scope message against. Null for standard policies.
   */
  prompt?: string | undefined;
  /**
   * Prompt-injection detection rule ids enabled in addition to the heuristic baseline. When empty, only heuristics run.
   */
  promptInjectionRules?: Array<string> | undefined;
  /**
   * CVSS-style severity (0.1-10) the author assigns to findings this policy produces. Descriptive only; changing it does not re-scan messages. Defaults to 5.
   */
  score: number;
  /**
   * Default disposition for shadow MCP blocking policies: block_all blocks every non-Speakeasy-hosted server unless allowed, allow_all permits every server unless blocked. Blocked URLs are stored as risk_policy:block grants, not on the policy. Immutable after create. Only present on policies with the shadow_mcp source and block action.
   */
  shadowMcpDisposition?: RiskPolicyShadowMcpDisposition | undefined;
  /**
   * Detection sources enabled for this policy.
   */
  sources: Array<string>;
  /**
   * When the policy was last updated.
   */
  updatedAt: Date;
  /**
   * Optional message shown to the end user when this policy blocks an action or surfaces a flagged finding. When unset, a default message is rendered.
   */
  userMessage?: string | undefined;
  /**
   * Policy version, incremented on each update.
   */
  version: number;
};

/** @internal */
export const RiskPolicyAction$inboundSchema: z.ZodMiniType<
  RiskPolicyAction,
  unknown
> = openEnums.inboundSchema(RiskPolicyAction);

/** @internal */
export const RiskPolicyAudienceType$inboundSchema: z.ZodMiniType<
  RiskPolicyAudienceType,
  unknown
> = openEnums.inboundSchema(RiskPolicyAudienceType);

/** @internal */
export const RiskPolicyPolicyType$inboundSchema: z.ZodMiniType<
  RiskPolicyPolicyType,
  unknown
> = openEnums.inboundSchema(RiskPolicyPolicyType);

/** @internal */
export const RiskPolicyShadowMcpDisposition$inboundSchema: z.ZodMiniType<
  RiskPolicyShadowMcpDisposition,
  unknown
> = openEnums.inboundSchema(RiskPolicyShadowMcpDisposition);

/** @internal */
export const RiskPolicy$inboundSchema: z.ZodMiniType<RiskPolicy, unknown> = z
  .pipe(
    z.object({
      action: z._default(RiskPolicyAction$inboundSchema, "flag"),
      approval_config: z.optional(RiskPolicyApprovalConfig$inboundSchema),
      approvals_server_setup: z.optional(
        RiskPolicyApprovalsServerSetup$inboundSchema,
      ),
      approved_email_domains: z.optional(z.array(z.string())),
      audience_principal_urns: z.array(z.string()),
      audience_type: z._default(
        RiskPolicyAudienceType$inboundSchema,
        "everyone",
      ),
      auto_name: z.boolean(),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      custom_rule_ids: z.optional(z.array(z.string())),
      detection_scopes: z.optional(z.array(RiskDetectionScope$inboundSchema)),
      disabled_rules: z.optional(z.array(z.string())),
      enabled: z.boolean(),
      id: z.string(),
      mcp_scope: z.optional(RiskMCPScope$inboundSchema),
      model_config: z.optional(RiskPolicyModelConfig$inboundSchema),
      name: z.string(),
      policy_type: z._default(RiskPolicyPolicyType$inboundSchema, "standard"),
      presidio_entities: z.optional(z.array(z.string())),
      presidio_score_threshold: z.optional(z.number()),
      project_id: z.string(),
      prompt: z.optional(z.string()),
      prompt_injection_rules: z.optional(z.array(z.string())),
      score: z._default(z.number(), 5),
      shadow_mcp_disposition: z.optional(
        RiskPolicyShadowMcpDisposition$inboundSchema,
      ),
      sources: z.array(z.string()),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      user_message: z.optional(z.string()),
      version: z.int(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "approval_config": "approvalConfig",
        "approvals_server_setup": "approvalsServerSetup",
        "approved_email_domains": "approvedEmailDomains",
        "audience_principal_urns": "audiencePrincipalUrns",
        "audience_type": "audienceType",
        "auto_name": "autoName",
        "created_at": "createdAt",
        "custom_rule_ids": "customRuleIds",
        "detection_scopes": "detectionScopes",
        "disabled_rules": "disabledRules",
        "mcp_scope": "mcpScope",
        "model_config": "modelConfig",
        "policy_type": "policyType",
        "presidio_entities": "presidioEntities",
        "presidio_score_threshold": "presidioScoreThreshold",
        "project_id": "projectId",
        "prompt_injection_rules": "promptInjectionRules",
        "shadow_mcp_disposition": "shadowMcpDisposition",
        "updated_at": "updatedAt",
        "user_message": "userMessage",
      });
    }),
  );

export function riskPolicyFromJSON(
  jsonString: string,
): SafeParseResult<RiskPolicy, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskPolicy$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskPolicy' from JSON`,
  );
}
